import { InsertInvitation } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { CreateEvent, DistributionStatus } from '@thro-types/events';

import clsx from 'clsx';
import { addMinutes, endOfDay, getHours, getMinutes, isBefore } from 'date-fns';
import { Field, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import StyledDropzone from 'app/components/Dropzone';
import { translations } from 'locales/i18n';

import '../styles/EventModal.scss';

export default function EventCreation() {
  const { t } = useTranslation();
  const { touched, errors, setFieldValue, values, handleChange, handleBlur } = useFormikContext<
    CreateEvent & { distributionStatus: DistributionStatus }
  >();
  const today = new Date();

  const distributed =
    values.distributionStatus === DistributionStatus.DISTRIBUTED ||
    values.distributionStatus === DistributionStatus.DISTRIBUTING
      ? true
      : false;

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((field, text) => {
        setFieldValue(field, text);
      }, 500),
    [setFieldValue]
  );

  const handleRegistrationStartChange = date => {
    setFieldValue('registrationPeriodStart', date);

    const registrationEndValue = values.registrationPeriodEnd;
    if (registrationEndValue && registrationEndValue < date) {
      handleRegistrationEndChange(date);
    }
  };

  const handleRegistrationEndChange = date => {
    setFieldValue('registrationPeriodEnd', date);

    const startDateValue = values.startDate;
    if (startDateValue && startDateValue < date) {
      handleStartChange(date);
    }
  };

  const handleStartChange = (date: Date | null) => {
    const formattedDate =
      date && getHours(date) === 0 && getMinutes(date) === 0 ? addMinutes(date, 1) : date;

    if (formattedDate) {
      const endDateValue = values.endDate;
      if (endDateValue && isBefore(endDateValue, formattedDate)) {
        setFieldValue('startDate', formattedDate);
        setFieldValue('endDate', formattedDate);
      } else {
        setFieldValue('startDate', formattedDate);
      }
    }
  };

  const handleEndChange = (date: Date | null) => {
    if (date && getHours(date) === 0 && getMinutes(date) === 0) {
      setFieldValue('endDate', endOfDay(date));
    } else {
      setFieldValue('endDate', date);
    }
  };

  const handleVisibleFromChange = (date: Date | null) => {
    if (date && getHours(date) === 0 && getMinutes(date) === 0) {
      date.setMinutes(1);
    }
    setFieldValue('visibleFrom', date);
  };

  return (
    <Grid container className="event-modal__input">
      <FormControl required fullWidth>
        <FormLabel>{t(translations.pages.events.create.eventName())}</FormLabel>
        <Field
          as={TextField}
          id="name"
          name="name"
          fullWidth
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          inputProps={{ maxLength: 125 }}
        />
      </FormControl>
      <FormControl fullWidth required>
        <FormLabel>{t(translations.pages.events.create.description())}</FormLabel>
        <Field name="description">
          {({ field }) => (
            <>
              <ReactQuill
                className={clsx('event-modal__rte', {
                  'event-modal__rte--error': touched.description && errors.description
                })}
                value={field.value}
                onChange={(_content, _delta, _source, editor) =>
                  debouncedChangeHandler('description', editor.getHTML())
                }
                theme="snow"
                modules={{
                  toolbar: [
                    ['bold', 'italic'],
                    ['link'],
                    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                    ['clean']
                  ],
                  clipboard: {
                    allowed: {
                      tags: [
                        'a',
                        'b',
                        'strong',
                        'u',
                        's',
                        'i',
                        'p',
                        'br',
                        'ul',
                        'ol',
                        'li',
                        'span'
                      ],
                      attributes: ['href', 'rel', 'target', 'class']
                    },
                    keepSelection: true,
                    substituteBlockElements: true,
                    magicPasteLinks: true,
                    matchVisual: false
                  }
                }}
                formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
              />
              {touched.description && errors.description && (
                <FormHelperText error>{errors.description}</FormHelperText>
              )}
            </>
          )}
        </Field>
      </FormControl>
      <FormControl fullWidth required>
        <FormLabel>{`${t(translations.pages.events.create.host())} / ${t(
          translations.pages.events.create.lecturer()
        )}`}</FormLabel>
        <Field as={TextField} id="host" name="host" fullWidth value={values.host || ''} />
      </FormControl>
      <Grid container justifyContent={'flex-start'} spacing={2}>
        <Grid item xs={6}>
          <FormControl style={{ paddingTop: '1em' }}>
            <Tooltip
              title={t(translations.pages.events.tooltips.participantListVisible())}
              placement="bottom-start"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isParticipantListVisible}
                    onChange={handleChange}
                    name="isParticipantListVisible"
                  />
                }
                label={t(translations.pages.events.create.partListVisible())}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl style={{ paddingTop: '1em' }}>
            <Tooltip
              title={t(translations.pages.events.tooltips.isUnenrollAfterRegPeriodPossible())}
              placement="bottom-start"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isUnenrollAfterRegPeriodPossible}
                    onChange={handleChange}
                    name="isUnenrollAfterRegPeriodPossible"
                  />
                }
                label={t(translations.pages.events.create.isUnenrollAfterRegPeriodPossible())}
              />
            </Tooltip>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <FormLabel>{t(translations.pages.events.create.regPeriodStart())}</FormLabel>
            <DateTimePicker
              renderInput={props => (
                <TextField
                  {...props}
                  id="registrationPeriodStart"
                  name="registrationPeriodStart"
                  required
                  variant="outlined"
                  error={touched.registrationPeriodStart && Boolean(errors.registrationPeriodStart)}
                  helperText={
                    touched.registrationPeriodStart && <>{errors.registrationPeriodStart}</>
                  }
                  onBlur={handleBlur}
                />
              )}
              value={values.registrationPeriodStart}
              onChange={value => handleRegistrationStartChange(value)}
              mask="__.__.____ __:__"
              minDateTime={values.visibleFrom || today}
              inputFormat="dd.MM.yyyy HH:mm"
              ampm={false}
              ampmInClock={false}
              disabled={distributed}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <FormLabel>{t(translations.pages.events.create.regPeriodEnd())}</FormLabel>
            <DateTimePicker
              renderInput={props => (
                <TextField
                  {...props}
                  id="registrationPeriodEnd"
                  name="registrationPeriodEnd"
                  required
                  variant="outlined"
                  error={touched.registrationPeriodEnd && Boolean(errors.registrationPeriodEnd)}
                  helperText={touched.registrationPeriodEnd && <>{errors.registrationPeriodEnd}</>}
                  onBlur={handleBlur}
                />
              )}
              value={values.registrationPeriodEnd}
              onChange={value => handleRegistrationEndChange(value)}
              mask="__.__.____ __:__"
              minDateTime={values.registrationPeriodStart || today}
              inputFormat="dd.MM.yyyy HH:mm"
              ampm={false}
              ampmInClock={false}
              disabled={distributed}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <FormLabel>{t(translations.pages.events.create.startDate())}</FormLabel>
            <DateTimePicker
              renderInput={props => (
                <TextField
                  {...props}
                  id="startDate"
                  name="startDate"
                  required
                  variant="outlined"
                  error={touched.startDate && Boolean(errors.startDate)}
                  helperText={touched.startDate && <>{errors.startDate}</>}
                  onBlur={handleBlur}
                />
              )}
              value={values.startDate}
              onChange={value => handleStartChange(value)}
              mask="__.__.____ __:__"
              minDateTime={values.registrationPeriodEnd || today}
              inputFormat="dd.MM.yyyy HH:mm"
              ampm={false}
              ampmInClock={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <FormLabel>{t(translations.pages.events.create.endDate())}</FormLabel>
            <DateTimePicker
              renderInput={props => (
                <TextField
                  {...props}
                  id="endDate"
                  name="endDate"
                  required
                  variant="outlined"
                  error={touched.endDate && Boolean(errors.endDate)}
                  helperText={touched.endDate && <>{errors.endDate}</>}
                  onBlur={handleBlur}
                />
              )}
              value={values.endDate}
              onChange={value => handleEndChange(value)}
              mask="__.__.____ __:__"
              minDateTime={values.startDate || today}
              inputFormat="dd.MM.yyyy HH:mm"
              ampm={false}
              ampmInClock={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <FormLabel>{t(translations.pages.events.create.visibleFrom())}</FormLabel>
            <DateTimePicker
              renderInput={props => (
                <TextField
                  {...props}
                  id="visibleFrom"
                  name="visibleFrom"
                  required
                  variant="outlined"
                  error={touched.visibleFrom && Boolean(errors.visibleFrom)}
                  helperText={touched.visibleFrom && <>{errors.visibleFrom}</>}
                  onBlur={handleBlur}
                />
              )}
              value={values.visibleFrom}
              onChange={value => handleVisibleFromChange(value)}
              mask="__.__.____ __:__"
              minDateTime={today}
              inputFormat="dd.MM.yyyy HH:mm"
              ampm={false}
              ampmInClock={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Tooltip title={t(translations.pages.events.tooltips.calendarInvite())}>
            <FormControl fullWidth>
              <FormLabel>
                <Grid container gap={1}>
                  <InsertInvitation />
                  {t(translations.pages.events.create.calendarInviteTitle())}
                </Grid>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.calendarInvite}
                    onChange={(e, checked) => setFieldValue('calendarInvite', checked)}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t(translations.pages.events.create.calendarInviteText())}
                  </Typography>
                }
              />
            </FormControl>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <FormLabel>{t(translations.pages.events.create.attachment())}</FormLabel>
          <StyledDropzone
            name="attachments"
            value={values.attachments}
            setFieldValue={setFieldValue}
            errors={errors}
            placeholder={t(translations.pages.events.create.attachmentPlaceholder())}
            multiple
            className="event-modal__input__dropzone"
          />
        </FormControl>
      </Grid>
    </Grid>
  );
}
